/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import * as _ from 'lodash';

import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';

import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { ContactService } from '../../services/contact.service';
import { ContactIgnoreService } from '../../services/contact-ignore.service';
import { DriftService } from '../../services/drift.service';

import { GlobalMessageService } from '../../services/global-message.service';
import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-settings-privacy',
  templateUrl: './settings-privacy.component.html',
  styleUrls: ['./settings-privacy.component.scss']
})
export class SettingsPrivacyComponent implements OnInit {
  loadingPage: boolean = false;
  searchText: string = '';
  searchContact: any = null;
  contextMessage: string = 'Enter an email address above to search for a contact';
  contacts: any[] = [];
  exportData: string;
  importCsvFile: File = null;
  foundContactsFromCsv: any[] = [];
  unusedEmailAddressesFromCsv: any[] = [];
  csvRowsToProcess: number = 0;
  processing: boolean = false;
  @ViewChild('csvFileInput') csvFileInput: ElementRef;

  constructor(private sessionService: SessionService,
              private cacheService: CacheService,
              private modalService: BsModalService,
              private globalMessageService: GlobalMessageService,
              private driftService: DriftService,

              private contactService: ContactService,
              private contactIgnoreService: ContactIgnoreService) { }

  ngOnInit() {
    if (!this.cacheService.get('session')) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }

    this.modalService.onHide.subscribe(
      (result: any) => {
        this.processing = false;
      }
    );
  }

  search() {
    this.contactService.findByEmail(this.searchText).subscribe(
      (result: any) => {
        this.searchContact = result;
        if (!this.searchContact) {
          this.globalMessageService.message('The email address ' + this.searchText + ' doesn\'t exist on this account');
        }
      },
      e => {
        this.globalMessageService.error();
      }
    )
  }

  clear() {
    this.searchText = '';
    this.searchContact = null;
  }

  delete() {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        header: `Are you sure you want to delete '${me.searchContact.email_address}' from your account?`,
        message: 'This action will delete all references to this contact from Drift Email and prevent them from being recreated as a contact in the future.',
        yesBtn: 'Delete contact',
        yesBtnClass: 'btn-tone-low',
        confirm: function() {
          me.contactIgnoreService.saveNew({
            email_address: me.searchContact.email_address
          }).subscribe(
            (result: any) => {
              me.globalMessageService.message(`Contact will be deleted from our system within 24 hours.`);
              me.driftService.track('deleted-contact');
              me.clear();
              me.initView();
            },
            e => {
              me.globalMessageService.error();
            }
          )
        }
      }
    }));
  }

  clearCsv() {
    this.csvFileInput.nativeElement.value = '';
    this.resetCsvUpload();
  }

  resetCsvUpload() {
    this.importCsvFile = null;
    this.foundContactsFromCsv = [];
    this.unusedEmailAddressesFromCsv = [];
    this.csvRowsToProcess = 0;
    this.processing = false;
  }

  csvUpload(event: EventTarget) {
    this.resetCsvUpload();

    let eventObj: MSInputMethodContext = <MSInputMethodContext> event;
    let target: HTMLInputElement = <HTMLInputElement> eventObj.target;
    let files: FileList = target.files;
    this.importCsvFile = files[0];
    let reader = new FileReader();

    reader.readAsText(this.importCsvFile);

    reader.onload = () => {
      let csvData = reader.result;
      let csvRows = (<string>csvData).split(/\r\n|\n/);
      csvRows = _.union(_.filter(csvRows, (row) => _.includes(row, '@')));
      this.csvRowsToProcess = csvRows.length;

      Promise.all(csvRows.map(emailAddress => {
        this.contactService.findByEmail(emailAddress).subscribe(
          (result: any) => {
            if(result && result.email_address) {
              this.foundContactsFromCsv.push(result);
            } else {
              this.unusedEmailAddressesFromCsv.push(emailAddress);
            }
            this.csvRowsToProcess--;
          },
          e => {
            this.globalMessageService.error();
            this.csvRowsToProcess--;
          }
        );
      }));
    };

    reader.onerror = () => {
      this.globalMessageService.error();
    };

  }

  csvDelete() {
    let me = this;
    let emailsDeleted = 0;
    me.processing = true;

    me.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        header: `Are you sure you want to delete ${me.foundContactsFromCsv.length} contacts from your account?`,
        message: 'This action will delete all references to these contacts from Drift Email and prevent them from being recreated as contacts in the future.',
        yesBtn: 'Delete contacts',
        yesBtnClass: 'btn-tone-low',
        confirm: function() {
          Promise.all(me.foundContactsFromCsv.map(contact => {
            me.contactIgnoreService.saveNew({
              email_address: contact.email_address
            }).subscribe(
              (result: any) => {
                me.driftService.track('deleted-contact');

                me.contacts = _.unionBy(me.contacts, [result], (value) => {return value.email_address});
                _.remove(me.foundContactsFromCsv, { email_address: result.email_address});
                emailsDeleted++;

                // We've finished adding all emails to the contact_ignore table
                if(me.foundContactsFromCsv.length <= 0) {
                  me.globalMessageService.message(`${emailsDeleted} contacts will be deleted from our system within 24 hours.`);
                  me.clearCsv();
                }
              },
              e => {
                me.globalMessageService.error();
              }
            )
          }))
        }
      }
    }));
  }

  export() {
    this.globalMessageService.message('Your export should download in the next few seconds');
    let temp = `Contact email,Deleted by,Date deleted\n`;
    this.contacts.forEach(contact => {
      let d = new Date(contact.created_at);
      let dateStr = (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear() + ' ' + d.getHours() + ':' + d.getMinutes() + ':' + d.getSeconds();
      temp += `"${contact.email_address}","${contact.deleted_by_email_address}",${dateStr}\n`;
    });

    this.exportData = temp;
  }

  private initView() {
    this.loadingPage = true;
    this.contactIgnoreService.getAll().subscribe(
      (result: any) => {
        this.contacts = result;
        this.loadingPage = false;
      },
      e => {
        this.globalMessageService.error();
      }
    )
  }
}



// WEBPACK FOOTER //
// ./client/app/settings/settings-privacy/settings-privacy.component.ts